"use client";

import {
	Badge,
	Button,
	Heading,
	Image,
	Price,
	Text
} from "@pamperedchef/design-system";
import type { Cta, CmsImage } from "@/composites/landing/types";
import type { KitProductView } from "../KitDetailsModal";
import * as styles from "./KitCard.module.scss";

/**
 * CMS payload shape for one kit card. Mirrors the `kit_options.kits[]` entry —
 * the `KitOptionsGrid` reads this off the block and feeds the flat `KitCardProps`
 * to each `KitCard`. Kept separate from `KitCardProps` so the composite stays
 * agnostic of the CMS envelope while the dispatcher does the shape translation.
 */
export interface KitCardData {
	badge_text?: string;
	kit_product: KitProductView;
	price_text?: string;
	marketing_description?: string;
	image?: CmsImage;
	select_cta?: Cta;
	view_details_label?: string;
}

/**
 * Best-effort parse of fixture-style price strings (e.g. `"$50.00"`) into a
 * numeric value for the DS `Price` component. Returns `undefined` when the
 * string can't be coerced — caller renders nothing in that case.
 */
function parsePriceText(text: string | undefined): number | undefined {
	if (!text) return undefined;
	const cleaned = text.replace(/[^0-9.\-]/g, "");
	if (!cleaned) return undefined;
	const value = Number.parseFloat(cleaned);
	return Number.isFinite(value) ? value : undefined;
}

export interface KitCardProps {
	title: string;
	badgeText?: string;
	priceText?: string;
	marketingDescription?: string;
	image?: { url: string; alt?: string };
	selectCta?: Cta;
	viewDetailsLabel?: string;
	/** Called when the "view details" affordance is activated. */
	onViewDetails?: () => void;
}

/**
 * One kit option in the kit-options grid — a light mint-tint card with an
 * image, an optional dark "PC'S PICK" badge, a teal serif title, bold price,
 * marketing copy, an outlined "Select" CTA, and a tertiary "See what's
 * included" link that fires `onViewDetails`. Client island — it attaches the
 * details handler that the parent grid uses to open the modal.
 */
export function KitCard({
	title,
	badgeText,
	priceText,
	marketingDescription,
	image,
	selectCta,
	viewDetailsLabel = "See what's included",
	onViewDetails
}: KitCardProps) {
	return (
		<article className={styles["kit-card"]} data-component="KitCard">
			{image ? (
				<div className={styles["kit-card__media"]}>
					<Image
						src={image.url}
						alt={image.alt ?? title}
						className={styles["kit-card__image"]}
						objectFit="cover"
					/>
					{badgeText ? (
						<span className={styles["kit-card__badge"]}>
							<Badge
								label={badgeText}
								kind="inverted"
								size="sm"
								icon={{ name: "guarantee" }}
							/>
						</span>
					) : null}
				</div>
			) : null}

			<div className={styles["kit-card__body"]}>
				<div className={styles["kit-card__info"]}>
					<Heading
						tag="h3"
						styledAs="h4"
						className={styles["kit-card__title"]}
					>
						{title}
					</Heading>

					{priceText ? (
						// TODO STORE-007: swap to <PriceDisplay /> from @/composites/product/PriceDisplay
						// once that stub is implemented (currently returns a placeholder Stub).
						<Price
							className={styles["kit-card__price"]}
							display={parsePriceText(priceText)}
							size="large"
							theme="dark"
						/>
					) : null}

					{marketingDescription ? (
						<Text
							tag="p"
							styledAs="mdr"
							className={styles["kit-card__description"]}
						>
							{marketingDescription}
						</Text>
					) : null}
				</div>

				<div className={styles["kit-card__actions"]}>
					{selectCta ? (
						<Button
							kind="secondary"
							size="lg"
							onPress={() => {
								window.location.href = selectCta.href;
							}}
						>
							{selectCta.title}
						</Button>
					) : null}

					{onViewDetails ? (
						<Button
							kind="tertiary"
							size="sm"
							onPress={onViewDetails}
						>
							{viewDetailsLabel}
						</Button>
					) : null}
				</div>
			</div>
		</article>
	);
}
